/**
 * Real HTTP integration for the "HMI Configuration" backend service - see
 * `my-docs/docs/IndustrialSetup/HmiServerDrivenUi/overview-architecture.md`.
 * Each Digital Twin owns at most one configuration document, addressed by
 * `solutionId` / `siteId` / `twinId` - plus the record's own
 * `hmiConfigurationId` for update/delete.
 *
 * Wire shape (confirmed against the service):
 * ```
 * GET    /hmiconfigurations/solutions/{solutionId}/site/{siteId}/twin/{twinId}
 *        -> HMIConfigurationRecord[]  (always 0 or 1 entries for a twin)
 * POST   /hmiconfigurations/solutions/{solutionId}/site/{siteId}/twin/{twinId}
 *        <- { name, layout }, creates the twin's one-and-only record
 * PUT    /hmiconfigurations/solutions/{solutionId}/site/{siteId}/twin/{twinId}/{hmiConfigurationId}
 *        <- { name, layout }, replaces it
 * DELETE /hmiconfigurations/solutions/{solutionId}/site/{siteId}/twin/{twinId}/{hmiConfigurationId}
 * ```
 * `layout` is stored as an opaque JSON document, so every template-level
 * property this app needs (`pollIntervalSec`, `rules`, `computedFormulas`,
 * ...) rides along inside it even though today's sample payloads only show
 * `model`/`screens`/`groups`/`fields`.
 */
import { generatePath } from 'react-router-dom';
import { pepApiRequest, RequestMethod } from 'src/services';
import { HMITemplate } from './types';

const HMI_CONFIG_PATH = '/hmiconfigurations/solutions/:solutionId/site/:siteId/twin/:twinId';
const HMI_CONFIG_ITEM_PATH = `${HMI_CONFIG_PATH}/:configId`;

function configUrl(solutionId: string, siteId: string, twinId: string): string {
  return generatePath(HMI_CONFIG_PATH, { solutionId, siteId, twinId });
}

/** Url for a single configuration document - update/delete need the record's own id appended. */
function configItemUrl(solutionId: string, siteId: string, twinId: string, configId: string): string {
  return generatePath(HMI_CONFIG_ITEM_PATH, { solutionId, siteId, twinId, configId });
}

/** Raw document shape returned/accepted by the HMI Configuration service. */
export interface HMIConfigurationRecord {
  id: string;
  solutionId: string;
  siteId: string;
  twinId: string;
  name: string;
  status?: string;
  layout: {
    model?: { dtdlId?: string };
    screens: HMITemplate['screens'];
    groups: HMITemplate['groups'];
    fields: HMITemplate['fields'];
    rules?: HMITemplate['rules'];
    computedFormulas?: HMITemplate['computedFormulas'];
    version?: number;
    description?: string;
    pollIntervalSec?: number;
  };
  createdBy?: string;
  createdOn?: string;
  updatedBy?: string;
  updatedOn?: string;
}

/** Maps the service's wire record onto the client-side `HMITemplate` shape the rest of `src/hmi` expects. */
export function recordToTemplate(record: HMIConfigurationRecord): HMITemplate {
  const { layout } = record;
  return {
    id: record.id,
    name: record.name,
    description: layout.description,
    version: layout.version ?? 1,
    modelIds: layout.model?.dtdlId ? [layout.model.dtdlId] : undefined,
    pollIntervalSec: layout.pollIntervalSec ?? 7,
    screens: layout.screens ?? [],
    groups: layout.groups ?? [],
    fields: layout.fields ?? [],
    rules: layout.rules,
    computedFormulas: layout.computedFormulas,
    createdAt: record.createdOn ?? new Date().toISOString(),
    updatedAt: record.updatedOn ?? new Date().toISOString(),
  };
}

/** Maps a client-side `HMITemplate` onto the `{ name, layout }` body the service's create/update endpoints expect. */
export function templateToLayoutBody(template: HMITemplate): Record<string, unknown> {
  return {
    name: template.name,
    layout: {
      model: template.modelIds?.[0] ? { dtdlId: template.modelIds[0] } : undefined,
      screens: template.screens,
      groups: template.groups,
      fields: template.fields,
      rules: template.rules,
      computedFormulas: template.computedFormulas,
      version: template.version,
      description: template.description,
      pollIntervalSec: template.pollIntervalSec,
    },
  };
}

/** Fetches the twin's configuration list (0 or 1 entries - a twin owns at most one). */
export async function fetchHmiConfigurations(
  solutionId: string,
  siteId: string,
  twinId: string
): Promise<HMITemplate[]> {
  const records: HMIConfigurationRecord[] = await pepApiRequest({
    method: RequestMethod.Get,
    url: configUrl(solutionId, siteId, twinId),
  });
  return (records ?? []).map(recordToTemplate);
}

/** Creates the twin's (first and only) configuration document. */
export async function createHmiConfiguration(
  solutionId: string,
  siteId: string,
  twinId: string,
  template: HMITemplate
): Promise<HMITemplate> {
  const record: HMIConfigurationRecord = await pepApiRequest({
    method: RequestMethod.Post,
    url: configUrl(solutionId, siteId, twinId),
    params: templateToLayoutBody(template),
  });
  return recordToTemplate(record);
}

/** Replaces the twin's existing configuration document (identified by its own `hmiConfigurationId`). */
export async function updateHmiConfiguration(
  solutionId: string,
  siteId: string,
  twinId: string,
  configId: string,
  template: HMITemplate
): Promise<HMITemplate> {
  const record: HMIConfigurationRecord = await pepApiRequest({
    method: RequestMethod.Put,
    url: configItemUrl(solutionId, siteId, twinId, configId),
    params: templateToLayoutBody(template),
  });
  return recordToTemplate(record);
}

/** Deletes the twin's configuration document, identified by its own `hmiConfigurationId`. */
export async function deleteHmiConfiguration(
  solutionId: string,
  siteId: string,
  twinId: string,
  configId: string
): Promise<void> {
  await pepApiRequest({
    method: RequestMethod.Delete,
    url: configItemUrl(solutionId, siteId, twinId, configId),
  });
}
